import { format, isToday, isYesterday } from "date-fns";

import type { EvidenceCompletedByResponseDto } from "@/generated/model/evidenceCompletedByResponseDto";
import type { EvidenceRecordResponseDto } from "@/generated/model/evidenceRecordResponseDto";
import { EvidenceSourceType } from "@/generated/model/evidenceSourceType";
import { EvidenceStatus } from "@/generated/model/evidenceStatus";
import type { EntityType } from "@/lib/icons/categories";

export type { SortDir } from "@/lib/category-display";

/** Sort columns supported server-side (must match the API allow-list). */
export type EvidenceSortColumn =
  | "date"
  | "type"
  | "activity"
  | "category"
  | "status"
  | "dueDate"
  | "completedBy";

export const PAGE_SIZE = 10;

/** Newest completions first when the user hasn't picked a column. */
export const DEFAULT_SORT: { column: EvidenceSortColumn; dir: "asc" | "desc" } =
  { column: "date", dir: "desc" };

/**
 * Category filter value for records with no primary category, sent on the same
 * `categoryId` param as a real id. Without it those records would be
 * unreachable, since the hub shows nothing until a category is picked.
 * Must match UNCATEGORISED_CATEGORY_ID in the API's list-evidence query.
 */
export const UNCATEGORISED_CATEGORY_ID = "UNCATEGORISED";

/** The filter state the Reset filters button clears. */
export interface EvidenceHubFilters {
  categoryId?: string;
}

/**
 * The cleared filter state. Location is absent by design: that control selects
 * the nursery for the whole session (the same state the top-bar switcher
 * writes), so resetting filters must not relocate the user — the convention
 * lib/events-hub.ts and lib/task-manager.ts document.
 */
export const EMPTY_FILTERS: EvidenceHubFilters = { categoryId: undefined };

/** Whether anything is filtered, so Reset can be disabled when it would no-op. */
export function hasActiveFilters(filters: EvidenceHubFilters): boolean {
  return !!filters.categoryId;
}

/** Shown before a category is picked — the hub is category-driven. */
export const PICK_CATEGORY_MESSAGE = "Choose a category to see its evidence.";

/** Shown once a category is picked but nothing in it has been completed. */
export const NO_MATCHES_MESSAGE = "No completed records in this category yet.";

/** Source → entity glyph (shape encodes the Type; colour comes from the category). */
export const SOURCE_ENTITY: Record<EvidenceSourceType, EntityType> = {
  [EvidenceSourceType.COMPLIANCE_TASK]: "compliance-action",
  [EvidenceSourceType.CHECKLIST]: "checklist",
};

/** Source → the Type column's display label. */
export const SOURCE_LABELS: Record<EvidenceSourceType, string> = {
  [EvidenceSourceType.COMPLIANCE_TASK]: "Compliance Action",
  [EvidenceSourceType.CHECKLIST]: "Checklist",
};

export const STATUS_DISPLAY: Record<EvidenceStatus, string> = {
  [EvidenceStatus.COMPLETED]: "Completed",
  [EvidenceStatus.COMPLETED_LATE]: "Completed late",
};

/** Route of the record's detail view, by source. */
export function detailRoute(
  record: Pick<EvidenceRecordResponseDto, "sourceType" | "recordId">,
): string {
  return record.sourceType === EvidenceSourceType.COMPLIANCE_TASK
    ? `/compliance/instance/${record.recordId}`
    : `/checklists/${record.recordId}`;
}

/** Completer display name, matching the platform convention (name, else email). */
export function completedByLabel(
  user?: EvidenceCompletedByResponseDto,
): string {
  if (!user) return "—";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email
  );
}

/** Completion timestamp label: "Today, 09:15" / "Yesterday, 15:45" / "10 Jun, 14:20". */
export function completedDateLabel(iso: string): string {
  const date = new Date(iso);
  if (isToday(date)) return `Today, ${format(date, "HH:mm")}`;
  if (isYesterday(date)) return `Yesterday, ${format(date, "HH:mm")}`;
  return format(date, "d MMM, HH:mm");
}

/** Due-date label: "Today" / "Yesterday" / "10 Jun 2024". */
export function dueDateLabel(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "d MMM yyyy");
}
